import { useState } from "react";
import {
  View,
  Text,
  TextInput,
  Pressable,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Shield } from "lucide-react-native";
import { haptic } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../onboarding/theme";
import {
  use2FAStatus,
  useAuthenticatePasskey,
  useRecover2FA,
  useVerify2FA,
} from "@src/packages/auth/hooks/use2FA";

// Both Stytch SDKs (@stytch/react on web, @stytch/react-native on native)
// throw errors carrying a structured `error_type` slug
// (stytch.com/docs/api/errors) — classify on that, never on message prose.
const stytchErrorType = (err: unknown): string | null =>
  err instanceof Error &&
  "error_type" in err &&
  typeof err.error_type === "string"
    ? err.error_type
    : null;

// Stytch error_type slugs meaning the user's code was wrong or expired, for
// both TOTP authenticate and recovery-code recover. `unable_to_authenticate_totp`
// is what stytch.totp.authenticate returns for a wrong code (captured live off
// dev). Anything else (network, session, rate limit) falls through to generic.
const CODE_ERROR_TYPES = new Set<string>([
  "unable_to_authenticate_totp",
  "totp_code_not_found",
  "unable_to_authenticate_totp_recovery_code",
  "totp_recovery_code_not_found",
  // Captured from the web SDK in QA run 29940341443 when an unknown recovery
  // code was submitted. Without this current slug the UI incorrectly falls
  // through to the generic transport-error copy.
  "invalid_recovery_code",
]);

export default function TwoFAChallengeScreen() {
  const t = useTranslations();
  const capturePress = usePressCapture("TwoFAChallengeScreen");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState<"totp" | "recovery">("totp");
  const [error, setError] = useState<string | null>(null);
  const verify2FA = useVerify2FA();
  const recover2FA = useRecover2FA();
  const authenticatePasskey = useAuthenticatePasskey();

  // Show only the factor(s) the user can actually use here, rather than always
  // offering both a TOTP input and a passkey button. A passkey is only offered
  // when one is registered FOR THIS HOST (passkeyUsableHere) — a passkey bound
  // to another domain can't be authenticated here, so surfacing it would just
  // dead-end. If we can't determine usable factors (status not ready), fall
  // back to the TOTP flow so the screen is never actionless.
  const { data: status } = use2FAStatus();
  const hasTotp = status?.totpEnrolled ?? false;
  const usablePasskey = status?.passkeyUsableHere ?? false;
  const showTotpFlow = hasTotp || !usablePasskey;
  const showPasskey = usablePasskey;

  const isPending =
    verify2FA.isPending ||
    recover2FA.isPending ||
    authenticatePasskey.isPending;

  // No explicit onSuccess: stytch.totp.authenticate adds the authenticator_app
  // factor to the session, useStytchSession re-emits, and the RootNavigator
  // unmounts this screen automatically.
  const handleVerify = async () => {
    capturePress(
      pressLabel(t("auth.two_fa_challenge.verify")),
      "auth.two_fa_challenge.verify",
    );
    try {
      setError(null);
      if (mode === "totp") {
        await verify2FA.mutateAsync({ totpCode: code });
      } else {
        await recover2FA.mutateAsync({ recoveryCode: code });
      }
      haptic("buzz");
    } catch (err) {
      // Classify on Stytch's structured `error_type` slug, never on message
      // prose — a wrong TOTP/recovery code carries one of the slugs below,
      // while a network/transport failure carries none, so we don't tell the
      // user their code was wrong when the request never landed. Mirrors the
      // CreateAccountScreen OTP fix.
      const errorType = stytchErrorType(err);
      const isCodeError = errorType !== null && CODE_ERROR_TYPES.has(errorType);
      if (isCodeError) {
        setError(
          mode === "totp"
            ? t("auth.two_fa_challenge.error_invalid_totp")
            : t("auth.two_fa_challenge.error_invalid_recovery"),
        );
      } else {
        setError(t("auth.two_fa_challenge.error_generic"));
      }
    }
  };

  // Passkey (WebAuthn) is a distinct factor CATEGORY ("hardware") from moon's
  // SMS first factor ("possession"), so a successful authenticate clears the
  // ≥2-category MFA gate. No explicit navigation here for the same reason as
  // the TOTP path: authenticate adds the webauthn factor to the session,
  // useStytchSession re-emits, and RootNavigator unmounts this screen.
  const handlePasskey = async () => {
    capturePress(
      pressLabel(t("auth.two_fa_challenge.use_passkey")),
      "auth.two_fa_challenge.use_passkey",
    );
    try {
      setError(null);
      await authenticatePasskey.mutateAsync();
      haptic("buzz");
    } catch {
      setError(t("auth.two_fa_challenge.error_passkey"));
    }
  };

  const isValid = mode === "totp" ? code.length === 6 : code.trim().length > 0;

  // Passkey-only users get a passkey-specific subtitle; otherwise the header
  // follows the TOTP/recovery mode as before.
  const title =
    !showTotpFlow || mode === "totp"
      ? t("auth.two_fa_challenge.title_totp")
      : t("auth.two_fa_challenge.title_recovery");
  const subtitle = !showTotpFlow
    ? t("auth.two_fa_challenge.subtitle_passkey")
    : mode === "totp"
      ? t("auth.two_fa_challenge.subtitle_totp")
      : t("auth.two_fa_challenge.subtitle_recovery");

  return (
    <SafeAreaView
      style={{
        flex: 1,
        backgroundColor: EMBER.colors.warmDark,
      }}
    >
      <View
        style={{
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          padding: 24,
        }}
      >
        <View
          style={{
            backgroundColor: EMBER.colors.surface,
            borderRadius: 20,
            padding: 32,
            width: "100%",
            maxWidth: 380,
            alignItems: "center",
          }}
        >
          <View
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: EMBER.colors.warm + "15",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20,
            }}
          >
            <Shield size={28} color={EMBER.colors.warm} />
          </View>

          <Text
            className="font-dm-sans"
            style={{
              fontSize: 20,
              fontWeight: "600",
              color: EMBER.colors.text,
              textAlign: "center",
              marginBottom: 8,
            }}
          >
            {title}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              textAlign: "center",
              lineHeight: 20,
              marginBottom: 24,
            }}
          >
            {subtitle}
          </Text>

          {error && (
            <Text
              style={{
                fontSize: 13,
                color: EMBER.colors.coral,
                textAlign: "center",
                marginBottom: 12,
              }}
            >
              {error}
            </Text>
          )}

          {showTotpFlow && (
            <>
              <TextInput
                value={code}
                onChangeText={(text) => {
                  setCode(
                    mode === "totp"
                      ? text.replace(/\D/g, "").slice(0, 6)
                      : text,
                  );
                  setError(null);
                }}
                placeholder={
                  mode === "totp"
                    ? "000000"
                    : t("auth.two_fa_challenge.placeholder_recovery")
                }
                keyboardType={mode === "totp" ? "number-pad" : "default"}
                maxLength={mode === "totp" ? 6 : undefined}
                autoCapitalize="none"
                autoCorrect={false}
                autoFocus
                style={{
                  width: "100%",
                  fontSize: mode === "totp" ? 24 : 16,
                  fontWeight: "600",
                  textAlign: "center",
                  letterSpacing: mode === "totp" ? 8 : 2,
                  paddingVertical: 14,
                  borderRadius: 12,
                  borderWidth: 1,
                  borderColor: error ? EMBER.colors.coral : EMBER.colors.border,
                  color: EMBER.colors.text,
                  marginBottom: 8,
                }}
              />

              <View style={{ height: 8 }} />

              <Pressable
                onPress={handleVerify}
                disabled={!isValid || isPending}
                accessibilityRole="button"
                testID="two-fa-challenge-verify"
                style={{
                  width: "100%",
                  paddingVertical: 14,
                  borderRadius: 12,
                  backgroundColor: EMBER.colors.warm,
                  alignItems: "center",
                  opacity: !isValid || isPending ? 0.5 : 1,
                }}
              >
                {isPending ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 15,
                      fontWeight: "600",
                      color: "#FFFFFF",
                    }}
                  >
                    {t("auth.two_fa_challenge.verify")}
                  </Text>
                )}
              </Pressable>

              <Pressable
                onPress={() => {
                  const toggleKey =
                    mode === "totp"
                      ? "auth.two_fa_challenge.use_recovery_code"
                      : "auth.two_fa_challenge.back_to_totp";
                  capturePress(pressLabel(t(toggleKey)), toggleKey);
                  setMode(mode === "totp" ? "recovery" : "totp");
                  setCode("");
                  setError(null);
                }}
                accessibilityRole="button"
                testID="two-fa-challenge-mode-toggle"
                style={{ marginTop: 16 }}
              >
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    color: EMBER.colors.warm,
                    fontWeight: "500",
                  }}
                >
                  {mode === "totp"
                    ? t("auth.two_fa_challenge.use_recovery_code")
                    : t("auth.two_fa_challenge.back_to_totp")}
                </Text>
              </Pressable>
            </>
          )}

          {showPasskey && (
            <Pressable
              onPress={handlePasskey}
              disabled={isPending}
              accessibilityRole="button"
              testID="two-fa-challenge-passkey"
              style={{
                marginTop: 16,
                width: "100%",
                paddingVertical: 12,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: EMBER.colors.warm,
                alignItems: "center",
                opacity: isPending ? 0.5 : 1,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 14,
                  fontWeight: "600",
                  color: EMBER.colors.warm,
                }}
              >
                {t("auth.two_fa_challenge.use_passkey")}
              </Text>
            </Pressable>
          )}
        </View>
      </View>
    </SafeAreaView>
  );
}
